import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
    Box,
    Typography,
    Paper,
    Button,
    IconButton,
    Chip,
    Stack,
    CircularProgress,
    Alert,
    Tooltip
} from '@mui/material';
import {
    ArrowLeft,
    CreditCard,
    Wallet,
    Building2,
    User,
    CheckCircle2,
    Clock,
    Copy,
    Check,
    MapPin,
    ShieldCheck
} from 'lucide-react';
import { getPaymentAccountById, getAllPaymentAccountsAdmin } from '../../Services/PaymentAccounts/PaymentAccountsApi';

// Brand Color Palette conforming to AGENTS.md
const FOREST = '#6E8964';
const SAGE_DARK = '#1B5E20';
const AMBER = '#D97706';
const AMBER_BG = '#FFFBEB';
const GREEN_BG = '#F0FDF4';
const GREEN_BORDER = '#BBF7D0';
const BORDER_COLOR = '#E2E8F0';
const TEXT_PRIMARY = '#1E293B';
const TEXT_SECONDARY = '#64748B';
const TEXT_MUTED = '#94A3B8';

const formatDateTime = (dateString) => {
    if (!dateString) return '—';
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    } catch {
        return dateString || '—';
    }
};

const formatUserWithType = (name, type) => {
    if (!name && !type) return '—';
    const formattedType = type ? ` (${type.charAt(0).toUpperCase() + type.slice(1)})` : '';
    if (name) return `${name}${formattedType}`;
    return type ? `(${type.charAt(0).toUpperCase() + type.slice(1)})` : '—';
};

const DetailRow = ({ label, value, isLast, copyable, isMono, isHighlight }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = (text) => {
        if (!text || text === '—') return;
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
    };

    const displayValue = value !== null && value !== undefined && value !== '' ? String(value) : '—';
    const canCopy = copyable && displayValue !== '—';

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                py: 0.75,
                pr: '3px',
                gap: 2
            }}
        >
            <Typography
                sx={{
                    fontSize: '0.825rem',
                    color: TEXT_SECONDARY,
                    fontWeight: 500,
                    flexShrink: 0
                }}
            >
                {label}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.75, minWidth: 0, textAlign: 'right' }}>
                <Typography
                    sx={{
                        fontSize: '0.875rem',
                        fontWeight: isHighlight ? 600 : 400,
                        color: displayValue === '—' ? TEXT_MUTED : TEXT_PRIMARY,
                        fontFamily: isMono ? 'monospace' : 'inherit',
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                        lineHeight: 1.4,
                        textAlign: 'right'
                    }}
                >
                    {displayValue}
                </Typography>
                {canCopy && (
                    <Tooltip title={copied ? "Copied!" : "Copy to clipboard"}>
                        <IconButton
                            size="small"
                            onClick={() => handleCopy(displayValue)}
                            sx={{
                                p: 0.4,
                                color: copied ? SAGE_DARK : TEXT_MUTED,
                                '&:hover': { color: TEXT_PRIMARY, bgcolor: 'transparent' }
                            }}
                        >
                            {copied ? <Check size={13} /> : <Copy size={13} />}
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
        </Box>
    );
};

const PaymentMethodDetails = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { id: paramId } = useParams();

    const targetId = paramId || location.state?.id || location.state?.account?.id || location.state?.paymentAccount?.id;
    const initialAccount = location.state?.account || location.state?.paymentAccount;

    const [account, setAccount] = useState(initialAccount || null);
    const [loading, setLoading] = useState(!initialAccount && Boolean(targetId));
    const [error, setError] = useState(null);

    const fetchAccountData = useCallback(async () => {
        if (!targetId) {
            setError("No payment account ID specified.");
            return;
        }

        try {
            setLoading(true);
            setError(null);
            
            // Fetch account directly by ID: GET /api/v1/payment-accounts/:id
            const res = await getPaymentAccountById(targetId);
            const accountData = Array.isArray(res?.data) ? res.data[0] : (res?.data || res?.account || (res?.id ? res : null));

            if (accountData) {
                setAccount(accountData);
            } else {
                // Fallback: Check in admin all list if direct response didn't contain record
                const allRes = await getAllPaymentAccountsAdmin({ page: 1, limit: 100 });
                const list = Array.isArray(allRes?.data) ? allRes.data : (allRes?.data?.accounts || []);
                const found = list.find((item) => String(item.id) === String(targetId));
                if (found) {
                    setAccount(found);
                } else {
                    setError(`Payment account #${targetId} not found.`);
                }
            }
        } catch (err) {
            console.error("Error loading payment account details:", err);
            // Graceful fallback to search in admin accounts list
            try {
                const allRes = await getAllPaymentAccountsAdmin({ page: 1, limit: 100 });
                const list = Array.isArray(allRes?.data) ? allRes.data : (allRes?.data?.accounts || []);
                const found = list.find((item) => String(item.id) === String(targetId));
                if (found) {
                    setAccount(found);
                    return;
                }
            } catch (fallbackErr) {
                console.error("Fallback search failed:", fallbackErr);
            }
            setError(err.response?.data?.message || "Failed to load payment account details.");
        } finally {
            setLoading(false);
        }
    }, [targetId]);

    useEffect(() => {
        if (targetId) {
            fetchAccountData();
        }
    }, [targetId, fetchAccountData]);

    const isPaypal = account?.methodType?.toLowerCase()?.includes('paypal');
    const isBank = account?.methodType?.toLowerCase()?.includes('bank');

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#FBFBFB', minHeight: '100vh' }}>
            {/* Top Navigation Bar */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Button
                    startIcon={<ArrowLeft size={16} />}
                    onClick={() => navigate('/payment-accounts')}
                    disableElevation disableFocusRipple disableRipple disableTouchRipple
                    sx={{
                        textTransform: 'none',
                        color: TEXT_SECONDARY,
                        fontSize: '0.85rem',
                        fontWeight: 500,
                        border: `1px solid ${BORDER_COLOR}`,
                        borderRadius: '6px',
                        px: 2,
                        py: 0.6,
                        bgcolor: '#FFFFFF',
                        '&:hover': { bgcolor: '#FFFFFF', borderColor: BORDER_COLOR }
                    }}
                >
                    Back to Payment Accounts
                </Button>
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 12 }}>
                    <CircularProgress size={32} sx={{ color: FOREST, mb: 2 }} />
                    <Typography sx={{ fontSize: '0.85rem', color: TEXT_SECONDARY }}>Loading payment account details...</Typography>
                </Box>
            ) : error ? (
                <Alert
                    severity="error"
                    sx={{
                        borderRadius: '8px',
                        border: '1px solid #FECACA',
                        bgcolor: '#FEF2F2',
                        color: '#991B1B',
                        fontSize: '0.85rem',
                        mb: 3
                    }}
                    action={
                        <Button color="inherit" size="small" onClick={fetchAccountData}>
                            Retry
                        </Button>
                    }
                >
                    {error}
                </Alert>
            ) : account ? (
                <Stack spacing={3}>
                    {/* Header Summary Banner */}
                    <Paper
                        elevation={0}
                        sx={{
                            p: { xs: 2.5, md: 3 },
                            borderRadius: '8px',
                            border: `1px solid ${BORDER_COLOR}`,
                            bgcolor: '#FFFFFF',
                            boxShadow: 'none'
                        }}
                    >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box
                                    sx={{
                                        width: 48,
                                        height: 48,
                                        borderRadius: '8px',
                                        bgcolor: isPaypal ? '#EFF6FF' : isBank ? '#F0FDF4' : '#F8FAFC',
                                        border: `1px solid ${isPaypal ? '#BFDBFE' : isBank ? '#BBF7D0' : BORDER_COLOR}`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: isPaypal ? '#1D4ED8' : isBank ? SAGE_DARK : TEXT_PRIMARY
                                    }}
                                >
                                    {isPaypal ? <Wallet size={22} /> : isBank ? <Building2 size={22} /> : <CreditCard size={22} />}
                                </Box>
                                <Box>
                                    <Typography sx={{ fontSize: '1.25rem', fontWeight: 600, color: TEXT_PRIMARY }}>
                                        {account.bankName || (isPaypal ? 'PayPal Account' : 'Payment Account')}
                                    </Typography>
                                </Box>
                            </Box>

                            {/* Status Badges */}
                            <Stack direction="row" spacing={1} flexWrap="wrap">
                                {account.isDefault && (
                                    <Chip
                                        size="small"
                                        label="Primary Default"
                                        sx={{
                                            bgcolor: AMBER_BG,
                                            color: AMBER,
                                            border: '1px solid #FDE68A',
                                            fontWeight: 500,
                                            fontSize: '0.72rem',
                                            height: 24
                                        }}
                                    />
                                )}
                                <Chip
                                    size="small"
                                    icon={account.isVerified ? <CheckCircle2 size={13} color={SAGE_DARK} /> : <Clock size={13} color={AMBER} />}
                                    label={account.isVerified ? "Verified Account" : "Pending Verification"}
                                    sx={{
                                        bgcolor: account.isVerified ? GREEN_BG : AMBER_BG,
                                        color: account.isVerified ? SAGE_DARK : AMBER,
                                        border: `1px solid ${account.isVerified ? GREEN_BORDER : '#FDE68A'}`,
                                        fontWeight: 500,
                                        fontSize: '0.72rem',
                                        height: 24,
                                        '& .MuiChip-icon': { ml: 0.5 }
                                    }}
                                />
                                <Chip
                                    size="small"
                                    label={String(account.methodType || 'Unknown').toUpperCase()}
                                    sx={{
                                        bgcolor: '#F8FAFC',
                                        color: TEXT_PRIMARY,
                                        border: `1px solid ${BORDER_COLOR}`,
                                        fontWeight: 500,
                                        fontSize: '0.72rem',
                                        height: 24
                                    }}
                                />
                            </Stack>
                        </Box>
                    </Paper>

                    {/* Unified Details List Section */}
                    <Paper
                        elevation={0}
                        sx={{
                            p: { xs: 2.5, md: 3.5 },
                            borderRadius: '8px',
                            border: `1px solid ${BORDER_COLOR}`,
                            bgcolor: '#FFFFFF',
                            boxShadow: 'none'
                        }}
                    >
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
                                gap: { xs: 4, lg: 0 }
                            }}
                        >
                            {/* LEFT COLUMN: Contains Account & Method Details, Billing / Registered Address, Audit Information (Created) */}
                            <Box sx={{ pr: { xs: 0, lg: 4 }, borderRight: { xs: 'none', lg: `1px solid ${BORDER_COLOR}` } }}>
                                {/* 1. Account & Method Details */}
                                <Box sx={{ pb: 3.5, mb: 3.5, borderBottom: `1px solid ${BORDER_COLOR}` }}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            bgcolor: '#F8FAFC',
                                            py: 0.9,
                                            px: 1.5,
                                            mb: 1.5,
                                            borderRadius: '6px',
                                            border: `1px solid ${BORDER_COLOR}`,
                                            minHeight: 36
                                        }}
                                    >
                                        <CreditCard size={15} color={FOREST} />
                                        <Typography
                                            sx={{
                                                fontSize: '0.72rem',
                                                fontWeight: 600,
                                                color: '#475569',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.6px'
                                            }}
                                        >
                                            Account & Method Details
                                        </Typography>
                                    </Box>

                                    <DetailRow label="Method Type" value={account.methodType} isHighlight />
                                    <DetailRow label="Account Currency" value={account.accountCurrency} isMono />
                                    <DetailRow label="Account Holder Name" value={account.accountHolderName} copyable />
                                    <DetailRow label="Account Holder Email" value={account.emailOfAccountHolder} copyable />
                                    <DetailRow label="Default Account" value={account.isDefault ? 'Yes' : 'No'} />
                                    <DetailRow label="Verification Status" value={account.isVerified ? 'Verified' : 'Unverified'} isLast />
                                </Box>

                                {/* 2. Billing / Registered Address */}
                                <Box sx={{ pb: 3.5, mb: 3.5, borderBottom: `1px solid ${BORDER_COLOR}` }}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            bgcolor: '#F8FAFC',
                                            py: 0.9,
                                            px: 1.5,
                                            mb: 1.5,
                                            borderRadius: '6px',
                                            border: `1px solid ${BORDER_COLOR}`,
                                            minHeight: 36
                                        }}
                                    >
                                        <MapPin size={15} color={FOREST} />
                                        <Typography
                                            sx={{
                                                fontSize: '0.72rem',
                                                fontWeight: 600,
                                                color: '#475569',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.6px'
                                            }}
                                        >
                                            Billing / Registered Address
                                        </Typography>
                                    </Box>

                                    <DetailRow label="Street Address" value={account.address} />
                                    <DetailRow label="City" value={account.city} />
                                    <DetailRow label="State / Province" value={account.state} />
                                    <DetailRow label="Country" value={account.country} />
                                    <DetailRow label="Pincode / Postal Code" value={account.pincode} isMono isLast />
                                </Box>

                                {/* 3. Audit Information */}
                                <Box>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            bgcolor: '#F8FAFC',
                                            py: 0.9,
                                            px: 1.5,
                                            mb: 1.5,
                                            borderRadius: '6px',
                                            border: `1px solid ${BORDER_COLOR}`,
                                            minHeight: 36
                                        }}
                                    >
                                        <ShieldCheck size={15} color={FOREST} />
                                        <Typography
                                            sx={{
                                                fontSize: '0.72rem',
                                                fontWeight: 600,
                                                color: '#475569',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.6px'
                                            }}
                                        >
                                            Audit Information
                                        </Typography>
                                    </Box>

                                    <DetailRow label="Created By" value={formatUserWithType(account.cuName, account.cuType)} isHighlight />
                                    <DetailRow label="Created At" value={formatDateTime(account.createdAt)} />
                                    <DetailRow label="Updated By" value={formatUserWithType(account.uuName, account.uuType)} isHighlight />
                                    <DetailRow label="Updated At" value={formatDateTime(account.updatedAt)} isLast />
                                </Box>
                            </Box>

                            {/* RIGHT COLUMN: Contains Banking & Transfer Details, Associated User */}
                            <Box sx={{ pl: { xs: 0, lg: 4 } }}>
                                {/* 1. Banking & Transfer Details */}
                                <Box sx={{ pb: 3.5, mb: 3.5, borderBottom: `1px solid ${BORDER_COLOR}` }}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            bgcolor: '#F8FAFC',
                                            py: 0.9,
                                            px: 1.5,
                                            mb: 1.5,
                                            borderRadius: '6px',
                                            border: `1px solid ${BORDER_COLOR}`,
                                            minHeight: 36
                                        }}
                                    >
                                        <Building2 size={15} color={FOREST} />
                                        <Typography
                                            sx={{
                                                fontSize: '0.72rem',
                                                fontWeight: 600,
                                                color: '#475569',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.6px'
                                            }}
                                        >
                                            Banking & Transfer Details
                                        </Typography>
                                    </Box>

                                    <DetailRow label="Bank Name" value={account.bankName} isHighlight />
                                    <DetailRow label="Account Name" value={account.accountName} />
                                    <DetailRow label="Account Number" value={account.accountNumber} copyable isMono />
                                    {account.ifsc && <DetailRow label="IFSC Code" value={account.ifsc} copyable isMono />}
                                    {account.swiftCode && <DetailRow label="SWIFT / BIC Code" value={account.swiftCode} copyable isMono />}
                                    {account.iban && <DetailRow label="IBAN" value={account.iban} copyable isMono />}
                                    <DetailRow label="Bank Country" value={account.bankCountry} />
                                    <DetailRow label="Currency Code" value={account.accountCurrency} isMono isLast />
                                </Box>

                                {/* 2. Associated User */}
                                <Box>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            bgcolor: '#F8FAFC',
                                            py: 0.9,
                                            px: 1.5,
                                            mb: 1.5,
                                            borderRadius: '6px',
                                            border: `1px solid ${BORDER_COLOR}`,
                                            minHeight: 36
                                        }}
                                    >
                                        <User size={15} color={FOREST} />
                                        <Typography
                                            sx={{
                                                fontSize: '0.72rem',
                                                fontWeight: 600,
                                                color: '#475569',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.6px'
                                            }}
                                        >
                                            Associated User
                                        </Typography>
                                    </Box>

                                    <DetailRow label="User Name" value={account.user?.name || account.cuName || account.accountHolderName || '—'} />
                                    <DetailRow label="Contact Email" value={account.emailOfAccountHolder || '—'} isLast />
                                </Box>
                            </Box>
                        </Box>
                    </Paper>
                </Stack>
            ) : null}
        </Box>
    );
};

export default PaymentMethodDetails;
